'use client';

import { NavigationType } from '@bells/schema/models/navigation';
import Link from 'next/link';
import { forwardRef, Fragment, memo, useMemo } from 'react';
import { Button } from '../../../components/button';
import {
  FooterBottom,
  FooterColumn,
  FooterContent,
  Footer as FooterCore,
} from '../../../components/footer';
import { Logo } from '../../../icons/logo';
import { Telegram } from '../../../icons/telegram';
import type { FooterProps } from './types';

import { Mail } from 'lucide-react';
import Image from 'next/image';
import { IconBrandSignal } from '../../../icons/signal';
import { layoutFooterTopLocations } from '@bells/i18n';

const FooterComponent = forwardRef<HTMLDivElement, FooterProps>(
  ({ items, bottomItems }, ref) => {
    const groupColumns = useMemo(() => {
      return (items ?? [])
        .filter((df) => df.type === NavigationType.GROUP)
        .map((group) => (
          <Fragment key={group.id}>
            {(group.childs ?? [])
              .filter((df) => df.type === NavigationType.ITEM)
              .map((df) => (
                <Link
                  key={df.id}
                  prefetch={false}
                  href={df.url ?? ''}
                  className='rounded-md px-2 py-1 text-sm font-medium text-foreground hover:bg-muted md:text-[15px]'
                >
                  {df.title}
                </Link>
              ))}
          </Fragment>
        ));
    }, [items]);

    const bottomLinks = useMemo(() => {
      return (items ?? [])
        .filter((df) => df.type === NavigationType.ITEM)
        .map((item) => (
          <Link
            key={item.id}
            prefetch={false}
            className='hover:text-brand font-medium text-foreground hover:underline'
            href={item.url ?? ''}
          >
            {item.title}
          </Link>
        ));
    }, [items]);

    const bottomItemsLinks = useMemo(() => {
      return (bottomItems ?? [])
        .filter((df) => df.type === NavigationType.ITEM)
        .map((item) => (
          <Link
            key={item.id}
            prefetch={false}
            className='text-brand text-base font-medium hover:text-white lg:text-xl xl:text-2xl'
            href={item.url ?? ''}
          >
            {item.title}
          </Link>
        ));
    }, [items]);

    return (
      <footer
        className='dark w-full bg-[hsl(0,0%,12%)] text-foreground dark:bg-[hsl(0,0%,8%)]'
        ref={ref}
      >
        <div className='mx-auto my-0 w-full max-w-screen-xl gap-2 px-4 py-3 lg:px-8'>
          <FooterCore>
            <nav
              aria-label={layoutFooterTopLocations()}
              className='mb-4 flex flex-wrap gap-4 border-b py-4 pt-0 lg:mb-8 lg:pb-8'
            >
              {bottomItemsLinks}
            </nav>
            <FooterContent className='flex-col gap-2 md:flex-row md:gap-8'>
              <FooterColumn>
                <div className='flex items-center gap-4 pl-2 pt-1 md:pl-0'>
                  <Link href='/' className='overflow-hidden rounded-sm'>
                    <Logo className='text-brand' size={36} />
                  </Link>
                  <div className='flex flex-col gap-0'>
                    <h3 className='text-base font-bold'>
                      <Link href='/'>BB Escort</Link>
                    </h3>
                    <span className='text-paragraph text-xs'>Seit 2010</span>
                  </div>
                </div>
                <div className='flex gap-2'></div>
              </FooterColumn>
              <FooterColumn
                component='nav'
                aria-label='Customer service'
                className='flex w-full flex-1 flex-wrap gap-2 gap-x-1 md:flex-row md:items-center lg:justify-end'
              >
                {groupColumns}
              </FooterColumn>
            </FooterContent>

            <FooterBottom className='items-start md:items-center'>
              <div className='flex flex-wrap items-center gap-2'>
                <div className='flex flex-wrap items-center'>
                  <Button
                    asChild
                    variant={'ghost'}
                    className='text-foreground'
                    size={'icon'}
                  >
                    <Link target='_blank' href={'https://x.com/bb_escort'}>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        shapeRendering='geometricPrecision'
                        textRendering='geometricPrecision'
                        imageRendering='optimizeQuality'
                        fillRule='evenodd'
                        clipRule='evenodd'
                        viewBox='0 0 512 462.799'
                      >
                        <path
                          fill='currentColor'
                          d='M403.229 0h78.506L310.219 196.04 512 462.799H354.002L230.261 301.007 88.669 462.799h-78.56l183.455-209.683L0 0h161.999l111.856 147.88L403.229 0zm-27.556 415.805h43.505L138.363 44.527h-46.68l283.99 371.278z'
                        />
                      </svg>
                      <span className='sr-only'>Join us on Twitter</span>
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant={'ghost'}
                    className='text-foreground'
                    size={'icon'}
                  >
                    <Link
                      href={'mailto:info@skypleasureltd.com'}
                      target='_blank'
                    >
                      <Mail />
                      <span className='sr-only'>Send us an e-mail</span>
                    </Link>
                  </Button>

                  <Button
                    asChild
                    variant={'ghost'}
                    className='text-foreground'
                    size={'icon'}
                  >
                    <Link href={'https://t.me/bbescort'} target='_blank'>
                      <Telegram />
                      <span className='sr-only'>Join us on Telegram</span>
                    </Link>
                  </Button>

                  <Button
                    asChild
                    variant={'ghost'}
                    className='text-foreground'
                    size={'icon'}
                  >
                    <Link
                      href='https://signal.me/#eu/JAGRgW9JF-FyutDBi50L5eReo8lsf15z5-ojQxvZocq58H4j8UdDzIH9CNXKwHHk'
                      target='_blank'
                    >
                      <IconBrandSignal />
                      <span className='sr-only'>Join us on Signal</span>
                    </Link>
                  </Button>
                </div>

                <span>© {`${new Date().getFullYear()}`} BB Escort®</span>
              </div>

              <nav
                area-label='Footer Bottom Navigation'
                className='flex flex-wrap gap-4 md:items-center md:justify-end'
              >
                {bottomLinks}
                <a
                  target='_blank'
                  href='//www.dmca.com/Protection/Status.aspx?id=b13a5c11-a346-497d-9229-e005dd7cb363&refurl=http://bb-escort.de'
                  title='DMCA.com Protection Status'
                  className='dmca-badge'
                >
                  <Image
                    width={0}
                    height={0}
                    loading='lazy'
                    sizes='100vw'
                    style={{ width: 'auto', height: 25 }}
                    src='//images.dmca.com/Badges/dmca-badge-w150-5x1-01.png?ID=//www.dmca.com/Protection/Status.aspx?id=b13a5c11-a346-497d-9229-e005dd7cb363'
                    alt='DMCA.com Protection Status'
                    unoptimized
                  />
                </a>
              </nav>
            </FooterBottom>
          </FooterCore>
        </div>
      </footer>
    );
  }
);

FooterComponent.displayName = 'Footer';
export const Footer = memo(FooterComponent);
